<template>
  <header class="rk-header" :class="{ 'is-sticky': isSticky, 'menu-open': isMobileMenuOpen }">
    <div class="rk-container rk-header__inner">
      <router-link to="/" class="rk-brand" @click="closeMobileMenu">
        <span class="rk-brand__mark"><i class="bi bi-building"></i></span>
        <span class="rk-brand__text">Rental<em>Kenya</em></span>
      </router-link>

      <nav class="rk-nav">
        <a href="#home" class="rk-nav__link" @click="scrollToSection('home')">Home</a>
        <a href="#features" class="rk-nav__link" @click="scrollToSection('features')">Features</a>
        <a href="#trust" class="rk-nav__link" @click="scrollToSection('trust')">Why Us</a>
        <a href="#about" class="rk-nav__link" @click="scrollToSection('about')">About</a>
      </nav>

      <div class="rk-header__actions">
        <router-link to="/login" class="rk-btn rk-btn--ghost rk-btn--sm">
          <i class="bi bi-box-arrow-in-right"></i>
          <span>Get in</span>
        </router-link>
      </div>

      <button
        class="rk-burger"
        @click="toggleMobileMenu"
        aria-label="Toggle menu"
        :aria-expanded="isMobileMenuOpen"
      >
        <span class="rk-burger__icon" :class="{ 'is-open': isMobileMenuOpen }">
          <span></span><span></span><span></span>
        </span>
      </button>
    </div>

    <div class="rk-overlay" :class="{ 'is-active': isMobileMenuOpen }" @click="closeMobileMenu"></div>

    <nav class="rk-mobile" :class="{ 'is-active': isMobileMenuOpen }">
      <div class="rk-mobile__head">
        <span class="rk-brand__text rk-brand__text--light">Rental<em>Kenya</em></span>
        <button class="rk-mobile__close" @click="closeMobileMenu" aria-label="Close menu">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="rk-mobile__body">
        <router-link to="/" class="rk-mobile__link" @click="closeMobileMenu">
          <i class="bi bi-house"></i><span>Home</span>
        </router-link>
        <a href="#features-overview" class="rk-mobile__link" @click="scrollToSection('features')">
          <i class="bi bi-grid"></i><span>Features</span>
        </a>
        <a href="#security" class="rk-mobile__link" @click="scrollToSection('trust')">
          <i class="bi bi-patch-check"></i><span>Why Us</span>
        </a>
        <a href="#about" class="rk-mobile__link" @click="scrollToSection('about')">
          <i class="bi bi-info-circle"></i><span>About</span>
        </a>

        <div class="rk-mobile__divider"></div>

        <router-link to="/login" class="rk-mobile__cta" @click="closeMobileMenu">
          <i class="bi bi-box-arrow-in-right"></i><span>Get in</span>
        </router-link>

        <div class="rk-mobile__contact">
          <a href="tel:+254725029795" class="rk-mobile__contact-item">
            <i class="bi bi-telephone"></i><span>+254 725 029 795</span>
          </a>
          <a href="mailto:sales@rentalkenya.com" class="rk-mobile__contact-item">
            <i class="bi bi-envelope"></i><span>sales@rentalkenya.com</span>
          </a>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'

export default {
  name: 'HeaderSection',
  setup() {
    const isMobileMenuOpen = ref(false)
    const isSticky = ref(false)

    const toggleMobileMenu = () => { isMobileMenuOpen.value = !isMobileMenuOpen.value }
    const closeMobileMenu = () => { isMobileMenuOpen.value = false }

    const scrollToSection = (sectionId) => {
      closeMobileMenu()
      setTimeout(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          const headerHeight = document.querySelector('.rk-header').offsetHeight
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
        }
      }, 100)
    }

    const handleScroll = () => { isSticky.value = window.scrollY > 50 }
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen.value) closeMobileMenu()
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen.value) closeMobileMenu()
    }

    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
      window.addEventListener('resize', handleResize)
      document.addEventListener('keydown', handleKeyDown)
    })
    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('keydown', handleKeyDown)
    })

    return { isMobileMenuOpen, isSticky, toggleMobileMenu, closeMobileMenu, scrollToSection }
  }
}
</script>

<style scoped>
.rk-header {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 1000;
  padding: 1.1rem 0;
  background: rgba(247, 246, 242, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid transparent;
  transition: all 0.3s var(--ease);
}
.rk-header.is-sticky {
  padding: 0.7rem 0;
  background: rgba(247, 246, 242, 0.97);
  border-bottom: 1px solid var(--line);
  box-shadow: var(--shadow-soft);
}

.rk-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
.rk-header__inner { display: flex; align-items: center; justify-content: space-between; gap: 2rem; }

/* Brand */
.rk-brand { display: flex; align-items: center; gap: 0.65rem; text-decoration: none; flex-shrink: 0; }
.rk-brand__mark {
  width: 38px; height: 38px; border-radius: var(--radius-sm);
  background: var(--ink); color: var(--brass);
  display: flex; align-items: center; justify-content: center; font-size: 1.05rem;
}
.rk-brand__text {
  font-family: var(--font-body); font-weight: 800; font-size: 1.3rem;
  color: var(--ink); letter-spacing: -0.02em;
}
.rk-brand__text em {
  font-family: var(--font-display); font-style: italic; font-weight: 400;
  color: var(--brass);
}
.rk-brand__text--light { color: var(--paper); }
.rk-brand__text--light em { color: var(--brass-light); }

/* Desktop nav */
.rk-nav { display: flex; gap: 2.25rem; margin-left: auto; }
.rk-nav__link {
  font-family: var(--font-body); font-weight: 500; font-size: 0.95rem;
  color: var(--slate); text-decoration: none; position: relative; padding: 0.35rem 0;
  cursor: pointer; transition: color 0.25s var(--ease);
}
.rk-nav__link::after {
  content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 2px;
  background: var(--brass); transition: width 0.3s var(--ease);
}
.rk-nav__link:hover { color: var(--ink); }
.rk-nav__link:hover::after { width: 100%; }

/* Actions */
.rk-header__actions { flex-shrink: 0; }

.rk-btn {
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-body); font-weight: 600; text-decoration: none;
  border-radius: var(--radius-sm); cursor: pointer; border: none;
  transition: all 0.25s var(--ease); white-space: nowrap;
}
.rk-btn--sm { padding: 0.6rem 1.15rem; font-size: 0.9rem; }
.rk-btn--ghost { background: transparent; color: var(--ink); border: 1.5px solid var(--ink); }
.rk-btn--ghost:hover { background: var(--ink); color: var(--paper); }

/* Burger (mobile) */
.rk-burger { display: none; background: none; border: none; padding: 0.4rem; cursor: pointer; z-index: 1002; }
.rk-burger__icon { display: flex; flex-direction: column; justify-content: space-between; width: 22px; height: 16px; position: relative; }
.rk-burger__icon span { display: block; height: 2px; width: 100%; background: var(--ink); border-radius: 1px; transition: all 0.3s var(--ease); transform-origin: center; }
.rk-burger__icon.is-open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.rk-burger__icon.is-open span:nth-child(2) { opacity: 0; }
.rk-burger__icon.is-open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* Mobile overlay + drawer */
.rk-overlay {
  display: none; position: fixed; inset: 0; background: rgba(16, 27, 45, 0.55);
  backdrop-filter: blur(3px); z-index: 999; opacity: 0; transition: opacity 0.3s var(--ease);
}
.rk-overlay.is-active { display: block; opacity: 1; }

.rk-mobile {
  position: fixed; top: 0; right: -100%; width: 86%; max-width: 380px;
  height: 100vh; height: 100dvh; background: var(--ink); z-index: 1001;
  transition: right 0.4s var(--ease); display: flex; flex-direction: column;
  box-shadow: -10px 0 40px rgba(0,0,0,0.25);
}
.rk-mobile.is-active { right: 0; }

.rk-mobile__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.5rem; border-bottom: 1px solid rgba(255,255,255,0.1); flex-shrink: 0;
}
.rk-mobile__close { background: none; border: none; color: var(--paper); font-size: 1.2rem; cursor: pointer; padding: 0.4rem; }

.rk-mobile__body { flex: 1; padding: 1.25rem; display: flex; flex-direction: column; gap: 0.25rem; overflow-y: auto; }
.rk-mobile__link {
  display: flex; align-items: center; gap: 1rem; padding: 0.9rem 0.75rem;
  color: rgba(247,246,242,0.85); text-decoration: none; border-radius: var(--radius-sm);
  font-family: var(--font-body); font-weight: 500; transition: all 0.25s var(--ease);
}
.rk-mobile__link i { color: var(--brass); width: 18px; text-align: center; }
.rk-mobile__link:hover { background: rgba(255,255,255,0.06); color: var(--paper); }

.rk-mobile__divider { height: 1px; background: rgba(255,255,255,0.1); margin: 0.75rem 0; }

.rk-mobile__cta {
  display: flex; align-items: center; justify-content: center; gap: 0.6rem;
  background: var(--brass); color: var(--ink); font-weight: 700; text-decoration: none;
  padding: 0.9rem; border-radius: var(--radius-sm); font-family: var(--font-body);
}

.rk-mobile__contact { margin-top: auto; padding-top: 1.5rem; border-top: 1px solid rgba(255,255,255,0.1); display: flex; flex-direction: column; gap: 0.75rem; }
.rk-mobile__contact-item { display: flex; align-items: center; gap: 0.75rem; color: rgba(247,246,242,0.7); text-decoration: none; font-size: 0.9rem; }
.rk-mobile__contact-item i { color: var(--brass); }

@media (max-width: 860px) {
  .rk-nav, .rk-header__actions { display: none; }
  .rk-burger { display: block; }
}

body:has(.rk-header.menu-open) { overflow: hidden; }
</style>